import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import {
  createTheme,
  PaletteOptions,
  accordionClasses,
  PaletteColorOptions,
} from '@mui/material';
import { PropsWithChildren } from 'react';
import type {
  TypographyVariantsOptions as TypographyOptions,
  TypographyStyle,
} from '@mui/material/styles';
import { prefixer } from 'stylis';
import { AccordionArrow } from 'Components';

export const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer],
  prepend: true,
});

export type ButtonVariants =
  | 'primary1'
  | 'primary2'
  | 'secondary1'
  | 'secondary2';
export type TypographyVariants =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'body4'
  | 'body5'
  | 'body6'
  | 'body7'
  | 'body8';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    primary1: true;
    primary2: true;
    secondary1: true;
    secondary2: true;
    secondary: true;
    purple: true;
    textBtn: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h3Underline: true;
    body3: true;
    body4: true;
    body5: true;
    body6: true;
    body7: true;
    body8: true;
    h6: true;
    h7: true;
  }
}

declare module '@mui/material/styles' {
  interface ExtendedTypographyOptions {
    purple2: ExtendedPaletteOptions['purple2'];
    orange: ExtendedPaletteOptions['orange'];
  }

  interface Palette {
    purple2: Palette['primary'];
    orange: Palette['primary'];
  }

  interface TypeText {
    muted: string;
  }

  interface PaletteColor {
    shade_50?: string;
    shade_50_1?: string;
    shade_50_2?: string;
    shade_50_3?: string;
    shade_100?: string;
    shade_150?: string;
    shade_200?: string;
    shade_300?: string;
    shade_400?: string;
    shade_500?: string;
    shade_600?: string;
    shade_700?: string;
    shade_700_1?: string;
    shade_700_2?: string;
    shade_700_3?: string;
    shade_800?: string;
    shade_900?: string;
  }

  interface SimplePaletteColorOptions {
    shade_50?: string;
    shade_50_1?: string;
    shade_50_2?: string;
    shade_50_3?: string;
    shade_100?: string;
    shade_150?: string;
    shade_200?: string;
    shade_300?: string;
    shade_400?: string;
    shade_500?: string;
    shade_600?: string;
    shade_700?: string;
    shade_700_1?: string;
    shade_700_2?: string;
    shade_700_3?: string;
    shade_800?: string;
    shade_900?: string;
  }
}

interface ExtendedTypographyOptions extends TypographyOptions {
  h3Underline: TypographyStyle;
  body3: TypographyStyle;
  body4: TypographyStyle;
  body5: TypographyStyle;
  body6: TypographyStyle;
  body7: TypographyStyle;
  body8: TypographyStyle;
  h6: TypographyStyle;
  h7: TypographyStyle;
}

interface ExtendedPaletteOptions extends PaletteOptions {
  purple2: PaletteColorOptions;
  orange: PaletteColorOptions;
}

const defaultTheme = createTheme();

const paletteOptions = {
  success: {
    main: '#01A324',
  },
  error: {
    main: '#D12424',
  },
  primary: {
    main: '#1552E8',
    shade_50: '#F6F8FE',
    shade_50_1: '#F8F8FF',
    shade_50_2: '#EDEEFF',
    shade_50_3: '#F1F4FD',
    shade_100: '#E7EDFF',
    shade_150: '#C2D4FF',
    shade_200: '#B7CCFF',
    shade_300: '#5B86EF',
    shade_400: '#3A6EEC',
    shade_500: '#103EB0',
    shade_600: '#0D318B',
    shade_700: '#081E54',
    shade_700_1: '#2F3F85',
    shade_700_2: '#46569D',
    shade_700_3: '#012969',
    shade_800: '#04102E',
    shade_900: '#010309',
  },
  secondary: {
    light: '#FBFCFF',
    main: '#081E54',
  },
  text: {
    primary: '#344266',
    secondary: '#081E54',
    muted: '#656F8B',
  },
  purple2: {
    main: '#A020FF',
    shade_50: '#FBF6FF',
    shade_50_1: '#F5FBFF',
    shade_50_2: '#F7FCFF',
    shade_100: '#F4E4FF',
    shade_200: '#E4C1FF',
    shade_300: '#D59DFF',
    shade_400: '#B756FF',
    shade_500: '#861BD6',
    shade_600: '#6D16AD',
    shade_700: '#531185',
    shade_700_1: '',
    shade_700_2: '',
    shade_700_3: '',
    shade_800: '#3A0C5C',
    shade_900: '#200633',
  },
  orange: {
    main: '#FF8165',
    shade_50: '#FFFAF9',
    shade_50_1: '#F5FBFF',
    shade_50_2: '#F7FCFF',
    shade_100: '#FFF0ED',
    shade_200: '#FFDCD4',
    shade_300: '#FFC8BB',
    shade_400: '#FF9F8A',
    shade_500: '#D66C55',
    shade_600: '#AD5845',
    shade_700: '#854335',
    shade_700_1: '',
    shade_700_2: '',
    shade_700_3: '',
    shade_800: '#5C2E24',
    shade_900: '#1F0F0C',
  },
  background: { default: '#fff' },
};

const borderRadius = 24;

export const ThemeV2_0 = createTheme({
  direction: 'rtl',
  shape: {
    borderRadius: borderRadius,
  },
  breakpoints: {},
  palette: paletteOptions,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          direction: 'rtl',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          margin: 0,
          position: 'relative',
          padding: '0 24px',
          [defaultTheme.breakpoints.up('sm')]: {
            position: 'relative',
            padding: 0,
            margin: 0,
          },
          [defaultTheme.breakpoints.up('lg')]: {
            maxWidth: 'initial',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: paletteOptions.primary?.shade_100,
          borderRadius: '12px',
          '.MuiAlert-icon': { margin: 0 },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        sx: {
          backgroundColor: paletteOptions.primary.main,
          borderRadius: '12px',
          color: '#fff',
          transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
          '&:hover': {
            background: paletteOptions.purple2.main,
            color: '#fff',
            borderColor: paletteOptions.purple2.main,
          },
          '&:focus': {
            backgroundColor: paletteOptions.primary.shade_700,
            color: '#fff',
            borderColor: paletteOptions.primary.shade_700,
          },
        },
        disableFocusRipple: true,
      },
      variants: [
        { props: { variant: 'text' }, style: {} },
        {
          props: { variant: 'outlined' },
          style: {
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'primary' },
          style: {
            backgroundColor: paletteOptions.primary.main,
            borderRadius: '12px',
            color: '#fff',
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'primary1' },
          style: {
            width: '100%',
            height: '100%',
            color: '#fff',
            backgroundColor: paletteOptions.primary.main,
            border: `1px solid ${paletteOptions.primary.main}`,
            borderRadius: '100px',
            boxShadow: ',0px 2px 16px rgba(4, 16, 46, 0.08)',
            padding: '10px 24px',
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'primary2' },
          style: {
            width: '100%',
            height: '100%',
            color: '#fff',
            backgroundColor: paletteOptions.primary.main,
            border: '1px solid',
            borderColor: paletteOptions.primary.main,
            borderRadius: '8px',
            padding: '10px 24px',
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'secondary1' },
          style: {
            width: '100%',
            height: '100%',
            color: paletteOptions.primary.main,
            backgroundColor: '#fff',
            border: `1px solid ${paletteOptions.primary.main}`,
            borderRadius: '100px',
            boxShadow: '0px 2px 16px rgba(4, 16, 46, 0.08)',
            padding: '10px 24px',
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'secondary2' },
          style: {
            width: '100%',
            height: '100%',
            color: paletteOptions.primary.main,
            backgroundColor: '#fff',
            border: `1px solid ${paletteOptions.primary.main}`,
            borderRadius: '8px',
            padding: '16px 24px',
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            backgroundColor: 'white',
            border: `1px solid ${paletteOptions.primary.main}`,
            borderRadius: '12px',
            color: paletteOptions.primary.main,
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'purple' },
          style: {
            backgroundColor: paletteOptions.purple2.main,
            borderRadius: '12px',
            color: '#fff',
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            '&:hover': {
              background: paletteOptions.purple2.main,
              color: '#fff',
              borderColor: paletteOptions.purple2.main,
            },
            '&:focus': {
              backgroundColor: paletteOptions.primary.shade_700,
              color: '#fff',
              borderColor: paletteOptions.primary.shade_700,
            },
          },
        },
        {
          props: { variant: 'textBtn' },
          style: {
            borderRadius: '12px',
            color: paletteOptions.primary.main,
            textDecoration: 'underline',
            transition: `
              background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
          },
        },
      ],
      styleOverrides: {
        endIcon: {
          marginLeft: -2,
          marginRight: 8,
        },
        startIcon: {
          marginLeft: 8,
          marginRight: -2,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: paletteOptions.text.secondary,
          zIndex: 2000,
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          textDecorationColor: paletteOptions.primary.main,
        },
      },
    },
    MuiStack: {
      defaultProps: {
        width: '100%',
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#5B86EF',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& label': {
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0px',
            [defaultTheme.breakpoints.up('sm')]: {
              fontSize: '20px',
              fontWeight: '400',
              lineHeight: '24px',
              letterSpacing: '0px',
            },
          },
          // RTL fix: MUI v7 hardcodes right:9 for endAdornment with no RTL support
          '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
            right: 'auto',
            left: 9,
          },
          '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
            {
              paddingRight: 9,
              paddingLeft: 39,
            },
          '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
            {
              paddingRight: 9,
              paddingLeft: 65,
            },
        },
        input: {
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '18px',
          lineHeight: '24px',
          letterSpacing: '0px',
          [defaultTheme.breakpoints.up('sm')]: {
            fontSize: '20px',
            fontWeight: '400',
            lineHeight: '24px',
            letterSpacing: '0px',
          },
        },
        option: {
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '18px',
          lineHeight: '24px',
          letterSpacing: '0px',
          [defaultTheme.breakpoints.up('sm')]: {
            fontSize: '20px',
            fontWeight: '400',
            lineHeight: '24px',
            letterSpacing: '0px',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          [`&.${accordionClasses.expanded}`]: {
            margin: '0',
          },
        },
      },
      defaultProps: {
        slotProps: {
          heading: {
            component: 'div',
          },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <AccordionArrow />,
      },
    },
    MuiAvatar: {
      defaultProps: {
        variant: 'square',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          textAlign: 'right',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          [defaultTheme.breakpoints.up('sm')]: {
            fontWeight: 400,
          },
        },
        shrink: {
          transformOrigin: 'top right',
          '&.MuiInputLabel-outlined': {
            transform: 'translate(-14px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          right: 0,
          padding: '0px 14px',
          '&[data-shrink="true"]': {
            padding: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          marginRight: 0,
          paddingRight: 0,
          marginLeft: 0,
          margin: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontWeight: 400,

            '& ::placeholder': {
              color: '#39546A',
              opacity: 1,
            },
          },
        },
      },
    },
  },

  typography: {
    fontFamily: 'Assistant',
    h1: {
      fontSize: '38px',
      fontWeight: '700',
      lineHeight: '44px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '48px',
        fontWeight: '700',
        lineHeight: '52px',
        letterSpacing: '0px',
      },
    },
    h2: {
      fontStyle: 'normal',
      fontWeight: '700',
      fontSize: '32px',
      lineHeight: '38px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '40px',
        fontWeight: '700',
        lineHeight: '48px',
        letterSpacing: '0px',
      },
    },
    h3: {
      letterSpacing: '0px',
      fontWeight: '700',
      fontSize: '24px',
      lineHeight: '28px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '32px',
        fontWeight: '700',
        lineHeight: '40px',
        letterSpacing: '0px',
      },
    },
    h3Underline: {
      fontSize: '24px',
      fontWeight: '700',
      lineHeight: '28px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '32px',
        fontWeight: '700',
        lineHeight: '40px',
        letterSpacing: '0px',
      },
    },
    h4: {
      fontSize: '20px',
      fontWeight: '400',
      lineHeight: '24px',
      letterSpacing: '0em',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '28px',
        fontWeight: '400',
        lineHeight: '34px',
        letterSpacing: '0px',
      },
    },
    h5: {
      letterSpacing: '0px',
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '24px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '24px',
        fontWeight: '700',
        lineHeight: '30px',
        letterSpacing: '0px',
      },
    },
    h6: {
      letterSpacing: '0px',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '24px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '31px',
      },
    },
    h7: {
      fontSize: '20px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        letterSpacing: '0px',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '24px',
        lineHeight: '31px',
      },
    },
    body1: {
      fontSize: '18px',
      fontWeight: '700',
      lineHeight: '24px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '20px',
        fontWeight: '700',
        lineHeight: '24px',
        letterSpacing: '0px',
      },
    },
    body2: {
      fontSize: '18px',
      fontWeight: '600',
      lineHeight: '24px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '20px',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0px',
      },
    },
    body3: {
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '18px',
      lineHeight: '24px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '20px',
        fontWeight: '400',
        lineHeight: '24px',
        letterSpacing: '0px',
      },
    },
    body4: {
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '24px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '18px',
        fontWeight: '700',
        lineHeight: '22px',
        letterSpacing: '0px',
      },
    },
    body5: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '20px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '18px',
        fontWeight: '600',
        lineHeight: '22px',
        letterSpacing: '0px',
      },
    },
    body6: {
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '20px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '18px',
        fontWeight: '400',
        lineHeight: '22px',
        letterSpacing: '0px',
      },
    },
    body7: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '16px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '20px',
        letterSpacing: '0px',
      },
    },
    body8: {
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '16px',
      letterSpacing: '0px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '20px',
        letterSpacing: '0px',
      },
    },
  } as ExtendedTypographyOptions,
});

export function RTL(props: PropsWithChildren<{}>) {
  return <CacheProvider value={cacheRtl}>{props.children}</CacheProvider>;
}
